import React from 'react';
import './RunnerCv.css';

const PROFILE_SRC = '/images/profile.jpg';

const motionJobs = [
  {
    company: 'Keymotion.ai',
    companyHref: 'https://www.keymotion.ai/',
    location: null,
    badge: 'Founder',
    dates: 'Jul 2026 – present',
    title: 'Founder',
    bullets: [
      'Founded and built keymotion.ai, a browser-based editor where AI drafts motion graphics and users refine them in an editor before exporting',
      'Designed the product from concept to launch, including the prompt and reference workflow, the editor interface and the export formats',
      'Created a template library for kinetic type, lower thirds, logo reveals and social graphics, aimed at everyday editing work',
      'Developed the full stack solo: browser editor, AI generation, After Effects project export, billing and a credits system',
    ],
  },
  {
    company: 'The Adare Collection',
    companyHref: 'https://theadarecollection.com/',
    location: null,
    badge: 'Remote',
    dates: 'Mar 2025 – present',
    title: 'Digital Designer',
    bullets: [
      'Sole web designer for theadarecollection.com — luxury private rentals near Adare Manor for Ryder Cup 2027; own brand, UI, SEO and shipping end to end',
      'Built a componentised property system: one template powers the grid, detail pages, map pins and enquiry picker so new houses ship without redesigning each listing',
      'Designed for qualified commercial leads — enquiry form captures group size, budget, intended use and preferred homes, not tyre-kicker traffic',
      'Maintain the live inventory channel: availability badges, SEO per property, performance, analytics and cookie consent as houses come online',
    ],
  },
  {
    company: 'Truth Creative',
    companyHref: 'https://www.truth-creative.co.uk/',
    location: null,
    badge: 'Contract',
    dates: 'Jun 2025 – Feb 2026',
    title: 'Motion Designer & Videographer',
    groups: [
      {
        label: 'Motion & edit',
        bullets: [
          'Edited raw footage to final delivery: multicam sync, cut to script and colour grade',
          'Keyed and composited green screen footage and built motion graphics into the final edits',
          'Ran client review rounds and turned around amends to deadline',
        ],
      },
      {
        label: 'Filming & direction',
        bullets: [
          'Led small-crew branded shoots at client sites across the UK, owning the day from setup through wrap',
          'Set up lighting, camera and audio for client interviews, green screen capture and multicam shoots',
          'Conducted client interviews on camera and refined the script on shoot day',
        ],
      },
    ],
  },
  {
    company: 'Box Bear',
    companyHref: 'https://boxbear.co.uk/',
    location: null,
    badge: 'In-house studio',
    dates: 'May 2022 – May 2024',
    title: 'Motion Graphic Designer & Production Assistant',
    groups: [
      {
        label: 'Motion & 3D',
        bullets: [
          "Edited footage into informative videos shaped to each client's brief, from talking-head interviews to pieces led by 3D graphics",
          'Designed motion graphics in After Effects and 3D assets in Cinema 4D, assembled in Premiere Pro',
          'Responsible for organisation of media and versioning on each project through to final export',
        ],
      },
      {
        label: 'In the studio',
        bullets: [
          'Planned and filmed interviews with healthcare professionals for pharmaceutical clients',
          'Set up the in-house studio for shoots and live sessions, including cameras, lighting and audio',
          'Ran the multicamera and live-stream kit for virtual reality meetings, with healthcare professionals joining live from around the world',
        ],
      },
    ],
  },
  {
    company: 'Freelance',
    companyHref: 'https://www.linkedin.com/in/seanderham/',
    location: null,
    badge: 'Alongside employed roles',
    dates: 'Jun 2021 – May 2025',
    title: 'Motion Designer & Editor',
    bullets: [
      'Created motion graphic and typographic animations for internal meetings and events',
      'Edited multicamera interviews and motion content for clients across sectors',
      'Took projects from offline edit through colour and graphics to finished deliverables',
    ],
  },
];

const skills = [
  'After Effects, Cinema 4D and Blender: motion graphics, 3D and compositing',
  'Premiere Pro and DaVinci Resolve: edit, multicam sync, colour grade and delivery',
  'Camera, lighting and audio for interviews, multicam and green screen',
  'VR/AR asset prep alongside traditional 3D workflows',
  'Full-stack web: React, Node/APIs, payments and credits, hosting and CI. Built and launched keymotion.ai and theadarecollection.com',
  'Client reviews, media organisation, versioning and on-time delivery',
];

const JobEntry = ({ job }) => (
  <div className="runner-cv__job">
    <div className="runner-cv__job-top">
      <h3>
        {job.companyHref ? (
          <a href={job.companyHref} target="_blank" rel="noopener noreferrer">
            {job.company}
          </a>
        ) : (
          job.company
        )}
        {job.location ? (
          <span className="runner-cv__badge runner-cv__badge--quiet">
            {job.location}
          </span>
        ) : null}
        {job.badge ? <span className="runner-cv__badge">{job.badge}</span> : null}
      </h3>
      <span className="runner-cv__dates">{job.dates}</span>
    </div>
    <h4>{job.title}</h4>
    {job.groups ? (
      <div className="runner-cv__groups">
        {job.groups.map((group) => (
          <div className="runner-cv__group" key={group.label}>
            <p className="runner-cv__group-label">{group.label}</p>
            <ul className="runner-cv__bullets">
              {group.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    ) : job.bullets ? (
      <ul className="runner-cv__bullets">
        {job.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    ) : (
      <p>{job.body}</p>
    )}
  </div>
);

const MotionCv = () => {
  return (
    <div className="runner-cv-page runner-cv-page--embedded">
      <article className="runner-cv">
        <header className="runner-cv__header">
          <div className="runner-cv__intro">
            <h1 className="runner-cv__name">Sean Derham</h1>
            <p className="runner-cv__role">Designer &amp; Developer</p>
            <p className="runner-cv__location">
              London · Available immediately
            </p>
            <div className="runner-cv__contacts runner-cv__contacts--screen">
              <a href="tel:+447597368482">07597 368482</a>
              <a href="mailto:sjderham@protonmail.com">sjderham@protonmail.com</a>
              <a
                href="https://seanderham.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                seanderham.com
              </a>
              <a
                href="https://seanderham.com/showreel"
                target="_blank"
                rel="noopener noreferrer"
              >
                Showreel
              </a>
              <a
                href="https://www.linkedin.com/in/seanderham/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
            <div className="runner-cv__contacts runner-cv__contacts--print">
              <span>
                07597 368482 · sjderham@protonmail.com · seanderham.com ·
                linkedin.com/in/seanderham
              </span>
            </div>
          </div>
          <img
            className="runner-cv__photo"
            src={PROFILE_SRC}
            alt="Sean Derham"
            width={112}
            height={112}
          />
        </header>

        <section className="runner-cv__section">
          <h2>Profile</h2>
          <p>
            Designer and developer with four years delivering brand, motion and
            digital work for clients, from concept through to launch. I craft
            immersive visuals and brand-driven experiences across motion
            graphics, interactive design and web, and use AI as leverage in
            production. Comfortable owning a project end to end. Founder of{' '}
            <a
              href="https://www.keymotion.ai/"
              target="_blank"
              rel="noopener noreferrer"
            >
              keymotion.ai
            </a>
            , an AI motion graphics editor built for editors.
          </p>
        </section>

        <section className="runner-cv__section" id="cv-work">
          <h2>Work Experience</h2>
          <div className="runner-cv__jobs">
            {motionJobs.map((job) => (
              <JobEntry key={`${job.company}-${job.dates}`} job={job} />
            ))}
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Personal Projects</h2>
          <p>
            I shoot, edit and grade short films on my own Sony FX3, and keep
            building motion and 3D skills through personal work alongside client
            delivery.
          </p>
        </section>

        <section className="runner-cv__section">
          <h2>Skills &amp; Software</h2>
          <ul className="runner-cv__skill-list">
            {skills.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </section>

        <section className="runner-cv__section">
          <h2>Education</h2>
          <div className="runner-cv__job">
            <div className="runner-cv__job-top">
              <h3>University of Sussex</h3>
              <span className="runner-cv__dates">2018 – 2021</span>
            </div>
            <p>First Class Honours, Product Design</p>
          </div>
        </section>

        <p className="runner-cv__disclaimer">
          This CV may be kept on file and distributed for employment purposes.
        </p>
      </article>
    </div>
  );
};

export default MotionCv;
